import { Fragment, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import screenfull from 'screenfull';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { layoutSelector } from '../../../../app/features/layout/layout.slice';
import { useAuth } from '../../../../hooks/useAuth.hook';
import { useAppDispatch } from '../../../../hooks/redux.hook';
import { useLazyLogoutQuery } from '../../../../app/features/auth/auth.api';
import { clearCredentials } from '../../../../app/features/auth/auth.slice';
import { LogoutResponse } from '../../../../types/response/auth.response.type';
import {
  rxOpenAlertModal,
  rxOpenConfirmModal,
} from '../../../../app/features/common/common.slice';
import { Messages } from '../../../../utils/mapping/messages.mapping';
import {
  DEFAULT_CONFIRM_ACCEPT,
  DEFAULT_CONFIRM_CANCEL,
  EMPTY_TEXT,
} from '../../../../utils/enum/defaultParams.enum';
import { WARNING } from '../../../../utils/enum/responseStates.enum';
import FaIcon from '../../../../components/global/FaIcon';

/* eslint-disable jsx-a11y/anchor-is-valid */

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ');
}

const bgColorNav = 'bg-blue-600';

const DashboardHeader = () => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();

  const [isFullScreenActived, setIsFullScreenActived] = useState(false);
  const { pageTitle } = useSelector(layoutSelector);

  const [logout] = useLazyLogoutQuery();

  const onLogoutSession = async () => {
    try {
      const logoutResponse: LogoutResponse = await logout().unwrap();
      if (logoutResponse.closed) {
        dispatch(clearCredentials());
      } else {
        message.warn(logoutResponse.message);
      }
    } catch (error: any) {
      message.error('Ha ocurrido un error al cerrar la sesión');
    }
  };

  const onToggleFullScreen = () => {
    if (screenfull.isFullscreen) {
      screenfull.exit().then(() => {
        setIsFullScreenActived(false);
      });
    } else {
      screenfull.request().then(() => {
        setIsFullScreenActived(true);
      });
    }
  };

  const onOpenModalLogout = () => {
    dispatch(
      rxOpenConfirmModal({
        title: EMPTY_TEXT,
        message: Messages.logoutConfirm,
        callbackYes: () => onLogoutSession(),
        callbackNo: () => {},
        textButtonNo: DEFAULT_CONFIRM_CANCEL,
        textButtonYes: DEFAULT_CONFIRM_ACCEPT,
      }),
    );
  };

  const onOpenModalWip = () => {
    dispatch(
      rxOpenAlertModal({
        title: Messages.empty,
        message: Messages.workInProgress,
        type: WARNING,
        callbackOk: () => {},
      }),
    );
  };

  return (
    <Disclosure as="nav" className={`${bgColorNav} relative z-[60]`}>
      {({ open }) => (
        <div className="w-full h-20 flex items-center px-4">
          <div className="flex items-center h-16 w-full">
            <div className="flex items-center">
              {pageTitle.icon && (
                <i
                  className={`far fa-${pageTitle.icon} mr-2 fa-fw fa-lg text-white`}
                />
              )}
              <span className="font-bold text-lg text-white">
                {pageTitle.title === 'Principal'
                  ? `Bienvenido ${user?.names.split(' ')[0]}`
                  : pageTitle.title}
              </span>
              <span className="font-base text-lg text-white">
                {pageTitle.title === 'Principal'
                  ? `, ¿En qué podemos ayudarte?`
                  : EMPTY_TEXT}
              </span>
            </div>
            <div className="flex items-center ml-auto">
              {screenfull.isEnabled && (
                <button
                  type="button"
                  className={`relative rounded-full ${bgColorNav} p-1 text-white active:text-gray-400`}
                  onClick={onToggleFullScreen}
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Ver notificaciones</span>
                  {isFullScreenActived ? (
                    <FaIcon icon="compress fa-lg" />
                  ) : (
                    <FaIcon icon="expand fa-lg" />
                  )}
                </button>
              )}
              <button
                type="button"
                className={`relative rounded-full ${bgColorNav} p-1 text-white active:text-gray-400`}
              >
                <span className="absolute -inset-1.5" />
                <span className="sr-only">Ver notificaciones</span>
                <FaIcon icon="bell fa-lg" />
              </button>
              <Menu as="div" className="relative ml-3">
                <div>
                  {/* <Menu.Button className="relative flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">Open user menu</span>
                    {/* <img className="h-8 w-8 rounded-full" src={`${process.env.PUBLIC_URL}/${user?.avatar}`} alt="" /> */}
                  {/* </Menu.Button> */}

                  {/* Lo anterior lo comentamos porque al poner el nuevo logo se deformaba
                      lo dejamos comentado si es que desean regresar al anterior logo*/}
                  <Menu.Button className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">Open user menu</span>
                    <img
                      className="h-8 w-8 rounded-full object-contain p-1" // 'object-contain' evita que se deforme
                      src={`${process.env.PUBLIC_URL}/${user?.avatar}`}
                      alt=""
                    />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-[100] mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={onOpenModalWip}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700',
                          )}
                        >
                          Mi Perfil
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={onOpenModalWip}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700',
                          )}
                        >
                          Configuración
                        </a>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="#"
                          onClick={onOpenModalLogout}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700',
                          )}
                        >
                          Cerrar Sesión
                        </a>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      )}
    </Disclosure>
  );
};

export default DashboardHeader;
